import { useMemo } from "react";

import { shortDescriptionHtml } from "~/hec/pdp/short-description.logic";

import type { PdpShortDescriptionProps } from "./types";

/**
 * PDP info-column short description (COM-180): SFRA's
 * `shortDescriptionTruncated` — sanitised and cut to ~225 chars at a sentence
 * end — then "Read More" to the accordion block (org_hec + app_wdh
 * productDetails.isml). The site-then-catalog fallback is resolved by the
 * caller (`resolveShortDescription` in `~/hec/pdp/short-description.logic`).
 *
 * `hasReadMoreTarget` must be the same `hasAccordionBlock` result that gates
 * the block itself, so the link never points at a block that does not render.
 *
 * Extracted from `product-details.tsx` so the rendering is tested (COM-180
 * review): the helpers alone left the raw render and the link gate unguarded.
 */
export function PdpShortDescription({
  shortDescription,
  hasReadMoreTarget,
}: PdpShortDescriptionProps) {
  const html = useMemo(
    () => (shortDescription ? shortDescriptionHtml(shortDescription) : ""),
    [shortDescription],
  );
  if (!shortDescription) return null;

  return (
    <div>
      <div
        className="prose prose-sm max-w-none font-grotesque text-[0.9625rem] font-light leading-[1.58] text-foreground"
        data-testid="pdp-short-description"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {hasReadMoreTarget && (
        <a
          href="#collapsible-description"
          className="mt-2 block text-sm font-medium underline"
          data-testid="pdp-read-more"
        >
          Read More
        </a>
      )}
    </div>
  );
}
